import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import { t } from 'i18next';

import reducer from './reducer';

const initialState = {
    isDoingAjax: true,
    isInitialized: false,
    headers: [],
    step: 1,
    fileName: '',
    inError: [],
};

const useBatchPrint = () => {
    const [state, dispatch] = React.useReducer(reducer, initialState);
    const downloadRef = React.useRef();
    const [downloadHref, setDownloadHref] = React.useState();

    const reset = () => {
        dispatch({
            type: 'update',
            newState: initialState,
        });
    };

    const initializeBatchPrint = async (close) => {
        try {
            let _headers = await API.post('ShipyardAPI', '/finances/invoices/batch_headers', {
                body: {
                    action: 'print',
                }
            });
            dispatch({
                type: 'update',
                newState: {
                    isDoingAjax: false,
                    isInitialized: true,
                    headers: _headers.map(header => {
                        return {
                            ...header,
                            label: t(header.label)
                        }
                    }),
                },
            });
        } catch (e) {
            console.error(e);
            toast.error('API error.');
            close?.();
        }
    };

    const handleFileName = (val) => {
        dispatch({
            type: 'update',
            newState: {
                fileName: val,
            },
        });
    };

    const printInvoices = async (selected, close) => {
        let defaultFileName = `Batch File ${Date.now()}`;
        let id = toast.loading(`Printing invoices with filename ${state.fileName || defaultFileName}.pdf...`);
        dispatch({
            type: 'doingAjax',
        });
        let success = false;

        try {
            let response = await API.post('ShipyardAPI', '/finances/invoices/batch_print', {
                body: {
                    selected,
                    fileName: state.fileName || defaultFileName,
                },
            });

            if (response?.presignedBatchUrl) {
                setDownloadHref(response.presignedBatchUrl);
                downloadRef.current?.click();
            }
            toast.success(response?.message || 'API success.');
            success = true;
        } catch (e) {
            console.log(e);
            toast.error(e?.response?.data?.message || 'API error.');
            if(e?.response?.data?.inError instanceof Array) {
                dispatch({
                    type: 'update',
                    newState: {
                        inError: e.response.data.inError
                    }
                });
            }
        } finally {
            toast.dismiss(id);
            if (success) {
                close?.();
            } else {
                dispatch({
                    type: 'ajaxDone',
                });
            }
        }
    };

    const nextStep = () => {
        dispatch({
            type: 'update',
            newState: {
                step: 2,
            },
        });
    };

    const validateBatchPrint = async (selectedRows) => {
        let id = toast.loading('Validating batch print status...');
        let isValid = false;

        if (selectedRows.length === 0) {
            toast.error('Please select one or more rows to initiate the batch status update.');
            toast.dismiss(id);
            return isValid;
        }

        try {
            let { valid, message } = await API.post('ShipyardAPI', '/finances/invoices/batch_validate', {
                body: {
                    action: 'print',
                    selected: Array.from(selectedRows),
                },
            });

            if (valid) toast.success(message || 'Success.');
            else toast.error(message || 'Error.');

            isValid = valid;
        } catch (e) {
            toast.error('Error.');
            console.log(e);
        } finally {
            toast.dismiss(id);
        }

        return isValid;
    };

    return {
        ...state,
        downloadHref,
        downloadRef,
        reset,
        initializeBatchPrint,
        printInvoices,
        nextStep,
        handleFileName,
        validateBatchPrint,
    };
};

export default useBatchPrint;
